import { vnTemplate } from "../../../vendor/valnet/valnet_jslib/vnKnockoutJsTemplates/vnTemplate";
import { infoButton_template } from "../../../vendor/valnet/valnet_jslib/vnKnockoutJsTemplates/infoButton_template";

declare var ko: any;

/** One key for all instances: the KO template name `eeatScorePanel_template` is shared globally (first script wins). */
const EEAT_SCORE_PANEL_HELP_TOOLTIP_VM_KEY = "eeatScorePanelHelpTooltip";

const EEAT_SCORE_HELP_TOOLTIP_HTML = `<div class="eeat-score-help-tooltip">
<p>Content is evaluated using the EEAT framework: Experience, Expertise, Authoritativeness, and Trustworthiness. Each dimension is scored independently from 0\u201325, and the final EEAT score (0\u2013100) is the sum of all four.</p>
<div class="eeat-score-help-section">
<h4>Experience</h4>
<ul>
<li><strong>Data used:</strong> Article content</li>
<li><strong>What it measures:</strong>
<ul>
<li>Whether the content demonstrates real-world, first-hand, or lived experience</li>
</ul>
</li>
<li><strong>How it\u2019s assessed:</strong>
<ul>
<li>Presence of personal examples, real-world scenarios, or practical insights</li>
<li>Generic or rewritten content without concrete examples \u2192 lower scores</li>
<li>Clear, specific, and nuanced first-hand experience \u2192 higher scores</li>
</ul>
</li>
</ul>
</div>
<div class="eeat-score-help-section">
<h4>Expertise</h4>
<ul>
<li><strong>Data used:</strong> Article content + author information</li>
<li><strong>What it measures:</strong>
<ul>
<li>The depth, accuracy, and structure of knowledge in the content</li>
</ul>
</li>
<li><strong>How it\u2019s assessed:</strong>
<ul>
<li>Specificity, clarity, structure, and subject understanding</li>
<li>Surface-level or vague content \u2192 lower scores</li>
<li>Detailed, well-structured, and precise explanations \u2192 higher scores</li>
</ul>
</li>
</ul>
</div>
<div class="eeat-score-help-section">
<h4>Authoritativeness</h4>
<ul>
<li><strong>Data used:</strong> Author information + site information</li>
<li><strong>What it measures:</strong>
<ul>
<li>The credibility of the author and website</li>
</ul>
</li>
<li><strong>How it\u2019s assessed:</strong>
<ul>
<li>Identifiable author, relevant credentials, and clear site positioning</li>
<li>Minimal or unclear credibility signals \u2192 lower scores</li>
<li>Strong, relevant authority signals \u2192 higher scores</li>
</ul>
</li>
</ul>
</div>
<div class="eeat-score-help-section">
<h4>Trustworthiness</h4>
<ul>
<li><strong>Data used:</strong> Article content + author information + site information</li>
<li><strong>What it measures:</strong>
<ul>
<li>How clear, transparent, and reliable the content appears</li>
</ul>
</li>
<li><strong>How it\u2019s assessed:</strong>
<ul>
<li>Clarity, intent, tone, and transparency (e.g., disclaimers, sourcing)</li>
<li>Unclear or unsupported content \u2192 lower scores</li>
<li>Clear, balanced, and transparent content \u2192 higher scores</li>
</ul>
</li>
</ul>
</div>
</div>`;

function formatEeatPart(score: number | string | null | undefined): string {
  if (score === null || score === undefined || score === "" || isNaN(Number(score))) {
    return "—/25";
  }
  return `${Number(score)}/25`;
}

function formatEeatExplanation(text: string | null | undefined): string {
  if (text === null || text === undefined || String(text).trim() === "") {
    return "No explanation available.";
  }
  return String(text).trim();
}

/** Video (15) and Thread (16) templates — aligns with PHP `emaki_article_template::ID_*`. */
const EEAT_DISALLOWED_TEMPLATE_IDS: number[] = [15, 16];

export class eeatScorePanel_template extends vnTemplate {
  protected eeatHelpTooltip: infoButton_template;

  public setExternalScoreRoot(scoreRoot: any): void {
    const panelVm = this.getTemplateViewModel();
    if (panelVm && typeof panelVm.setExternalScoreRoot === "function") {
      panelVm.setExternalScoreRoot(scoreRoot);
    }
  }

  protected initPartial(): void {
    this.eeatHelpTooltip = new infoButton_template(
      EEAT_SCORE_PANEL_HELP_TOOLTIP_VM_KEY,
      this.viewModel,
      this.caller
    );
    this.eeatHelpTooltip.setTitle("How EEAT Scoring Works");
    this.eeatHelpTooltip.setContent(EEAT_SCORE_HELP_TOOLTIP_HTML);
    // infoButton registers on the parent viewModel, but this panel's template binds with `data: eeatScore`
    // (or `data: eeatScoreGuidelines`), so nested bindings must resolve the tooltip on that same object.
    const panelVm = this.viewModel[this.idElement];
    if (panelVm) {
      panelVm[EEAT_SCORE_PANEL_HELP_TOOLTIP_VM_KEY] =
        this.viewModel[EEAT_SCORE_PANEL_HELP_TOOLTIP_VM_KEY];
    }
  }

  protected buildViewModel(): any {
    const root = this.viewModel;
    const externalScoreRoot = ko.observable(null);
    const resolveScoreRoot = () => {
      const explicitRoot = externalScoreRoot();
      if (explicitRoot) {
        return explicitRoot;
      }

      if (root && typeof root.eeatExperienceScore === "function") {
        return root;
      }

      if (
        this.caller &&
        this.caller.articlesEdit &&
        typeof this.caller.articlesEdit.getTemplateViewModel === "function"
      ) {
        return this.caller.articlesEdit.getTemplateViewModel();
      }

      return root;
    };
    const showRecalculate = this.idElement !== "eeatScoreGuidelines";
    const self = this;

    const readScore = (key: string) => {
      const scoreRoot = resolveScoreRoot();
      return scoreRoot && typeof scoreRoot[key] === "function" ? scoreRoot[key]() : null;
    };

    const readExplanation = (key: string) => {
      const scoreRoot = resolveScoreRoot();
      return scoreRoot && typeof scoreRoot[key] === "function" ? scoreRoot[key]() : null;
    };

    const eeatRecalculateLoading = ko.pureComputed(() => {
      const scoreRoot = resolveScoreRoot();
      return scoreRoot && typeof scoreRoot.eeatRecalculateLoading === "function"
        ? scoreRoot.eeatRecalculateLoading()
        : false;
    });
    const eeatRecalculateEnabled = ko.pureComputed(() => !eeatRecalculateLoading());

    const experienceLine = ko.pureComputed(() => formatEeatPart(readScore("eeatExperienceScore")));
    const expertiseLine = ko.pureComputed(() => formatEeatPart(readScore("eeatExpertiseScore")));
    const authorityLine = ko.pureComputed(() => formatEeatPart(readScore("eeatAuthorityScore")));
    const trustLine = ko.pureComputed(() => formatEeatPart(readScore("eeatTrustScore")));

    const experienceExplanationText = ko.pureComputed(() =>
      formatEeatExplanation(readExplanation("eeatExperienceExplanation"))
    );
    const expertiseExplanationText = ko.pureComputed(() =>
      formatEeatExplanation(readExplanation("eeatExpertiseExplanation"))
    );
    const authorityExplanationText = ko.pureComputed(() =>
      formatEeatExplanation(readExplanation("eeatAuthorityExplanation"))
    );
    const trustExplanationText = ko.pureComputed(() =>
      formatEeatExplanation(readExplanation("eeatTrustExplanation"))
    );

    const totalLine = ko.pureComputed(() => {
      const parts = [
        readScore("eeatExperienceScore"),
        readScore("eeatExpertiseScore"),
        readScore("eeatAuthorityScore"),
        readScore("eeatTrustScore"),
      ];
      if (parts.some((v) => v === null || v === undefined || v === "" || isNaN(Number(v)))) {
        return "—/100";
      }
      const sum = parts.reduce((acc, v) => acc + Number(v), 0);
      return `${sum}/100`;
    });

    const isEeatBlockedByTemplate = ko.pureComputed(() => {
      const scoreRoot = resolveScoreRoot();
      if (!scoreRoot || typeof scoreRoot.idTemplate !== "function") {
        return false;
      }
      const raw = scoreRoot.idTemplate();
      if (raw === null || raw === undefined || raw === "") {
        return false;
      }
      const n = parseInt(String(raw), 10);
      if (isNaN(n)) {
        return false;
      }
      return EEAT_DISALLOWED_TEMPLATE_IDS.indexOf(n) !== -1;
    });

    return {
      experienceLine,
      expertiseLine,
      authorityLine,
      trustLine,
      experienceExplanationText,
      expertiseExplanationText,
      authorityExplanationText,
      trustExplanationText,
      totalLine,
      isEeatBlockedByTemplate,
      showRecalculateButton: showRecalculate,
      setExternalScoreRoot: (scoreRoot: any) => externalScoreRoot(scoreRoot),
      eeatRecalculateLoading,
      eeatRecalculateEnabled,
      onClickRecalculate: function () {
        if (eeatRecalculateLoading()) {
          return;
        }
        if (self.caller && typeof self.caller.recalculateArticleEeatScore === "function") {
          self.caller.recalculateArticleEeatScore();
        }
      },
    };
  }

  protected getTemplateHtml(): string {
    return /*html*/ `
        <div class="eeat-score-panel-inner">
            <div class="eeat-score-title-row">
                <strong class="form-sub-title eeat-score-heading">E-E-A-T Score</strong>
                <!-- ko if: !isEeatBlockedByTemplate() -->
                <span class="w-tooltips-adv pos-bottom short eeat-score-heading-tooltip">
                    ${this.eeatHelpTooltip.getHtmlBuild()}
                </span>
                <!-- /ko -->
            </div>
            <!-- ko if: isEeatBlockedByTemplate() -->
            <p class="eeat-score-ineligible-msg">This article is not currently eligible for this feature.</p>
            <!-- /ko -->
            <!-- ko if: !isEeatBlockedByTemplate() -->
            <table class="eeat-score-table">
                <tbody>
                    <tr>
                        <th scope="row" class="eeat-score-label">Experience</th>
                        <td class="eeat-score-value">
                            <div class="eeat-score-value-hover">
                                <span class="eeat-score-value-figure" data-bind="text: experienceLine"></span>
                                <div class="eeat-score-explanation-hover" aria-hidden="true">
                                    <div class="eeat-score-explanation-hover-inner" data-bind="text: experienceExplanationText"></div>
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <th scope="row" class="eeat-score-label">Expertise</th>
                        <td class="eeat-score-value">
                            <div class="eeat-score-value-hover">
                                <span class="eeat-score-value-figure" data-bind="text: expertiseLine"></span>
                                <div class="eeat-score-explanation-hover" aria-hidden="true">
                                    <div class="eeat-score-explanation-hover-inner" data-bind="text: expertiseExplanationText"></div>
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <th scope="row" class="eeat-score-label">Authoritativeness</th>
                        <td class="eeat-score-value">
                            <div class="eeat-score-value-hover">
                                <span class="eeat-score-value-figure" data-bind="text: authorityLine"></span>
                                <div class="eeat-score-explanation-hover" aria-hidden="true">
                                    <div class="eeat-score-explanation-hover-inner" data-bind="text: authorityExplanationText"></div>
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <th scope="row" class="eeat-score-label">Trustworthiness</th>
                        <td class="eeat-score-value">
                            <div class="eeat-score-value-hover">
                                <span class="eeat-score-value-figure" data-bind="text: trustLine"></span>
                                <div class="eeat-score-explanation-hover" aria-hidden="true">
                                    <div class="eeat-score-explanation-hover-inner" data-bind="text: trustExplanationText"></div>
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr class="eeat-score-total-row">
                        <th scope="row" class="eeat-score-label">Total</th>
                        <td class="eeat-score-value" data-bind="text: totalLine"></td>
                    </tr>
                </tbody>
            </table>
            <div class="eeat-score-actions" data-bind="visible: showRecalculateButton">
                <button type="button" class="btn-form" data-bind="event: { click: onClickRecalculate }, enable: eeatRecalculateEnabled">Generate E-E-A-T Score</button>
            </div>
            <!-- /ko -->
        </div>
        `;
  }

  protected getTemplateName(): string {
    return "eeatScorePanel_template";
  }

  protected getMainElementClasses(): Array<string> {
    return ["form-group", "eeat-score-panel"];
  }

  protected getMainElementType(): string {
    return "div";
  }
}



// WEBPACK FOOTER //
// ./js/components/templates/eeatScorePanel_template.ts